// contexts/voice/useVoiceNotifications.ts
// Hook for managing tracking, pickup, callback, and handover notifications

import { useState, useCallback } from 'react';
import {
  TrackingData,
  PickupConfirmationData,
  CallbackConfirmationData,
  HandoverData,
  AgentInfo,
} from './types';
import { DEFAULT_AGENT, VOICE_CONSTANTS } from './constants';

export function useVoiceNotifications() {
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [pickupConfirmation, setPickupConfirmation] = useState<PickupConfirmationData | null>(null);
  const [callbackConfirmation, setCallbackConfirmation] = useState<CallbackConfirmationData | null>(null);
  const [handoverData, setHandoverData] = useState<HandoverData | null>(null);
  const [currentAgent, setCurrentAgent] = useState<AgentInfo>(DEFAULT_AGENT);

  // Tracking
  const showTracking = useCallback((data: TrackingData) => {
    setTrackingData(data);
  }, []);

  const dismissTracking = useCallback(() => {
    setTrackingData(null);
  }, []);

  // Pickup confirmation
  const showPickupConfirmation = useCallback((data: PickupConfirmationData) => {
    setPickupConfirmation(data);
  }, []);

  const dismissPickupConfirmation = useCallback(() => {
    setPickupConfirmation(null);
  }, []);

  // Callback confirmation
  const showCallbackConfirmation = useCallback((data: CallbackConfirmationData) => {
    setCallbackConfirmation(data);
  }, []);

  const dismissCallbackConfirmation = useCallback(() => {
    setCallbackConfirmation(null);
  }, []);

  // Handover
  const initiateHandover = useCallback((data: Omit<HandoverData, 'status'>) => {
    setHandoverData({ ...data, status: 'transferring' });
    // After delay, complete the handover
    setTimeout(() => {
      setHandoverData(prev => prev ? { ...prev, status: 'connected' } : null);
      setCurrentAgent(data.newAgent);
    }, VOICE_CONSTANTS.HANDOVER_DELAY_MS);
  }, []);

  const dismissHandover = useCallback(() => {
    setHandoverData(null);
  }, []);

  // Agent change (direct, without handover animation)
  const changeAgent = useCallback((agent: AgentInfo) => {
    setCurrentAgent(agent);
  }, []);

  // Reset all notifications and agent (on disconnect)
  const resetNotifications = useCallback(() => {
    setTrackingData(null);
    setPickupConfirmation(null);
    setCallbackConfirmation(null);
    setHandoverData(null);
    setCurrentAgent(DEFAULT_AGENT);
  }, []);

  return {
    // State
    trackingData,
    pickupConfirmation,
    callbackConfirmation,
    handoverData,
    currentAgent,

    // Actions
    showTracking,
    dismissTracking,
    showPickupConfirmation,
    dismissPickupConfirmation,
    showCallbackConfirmation,
    dismissCallbackConfirmation,
    initiateHandover,
    dismissHandover,
    changeAgent,
    resetNotifications,
  };
}
